import React, { useContext } from "react";
import { IntlContext } from "context";
import FieldGroup, { FieldGroupProps } from "./Element/FieldGroup";
import FieldControl, { FieldControlProps } from "./Element/FieldControl";
import Checkbox, { CheckboxProps } from "./Element/Checkbox";
import Label, { LabelProps } from "./Element/Label";
import Feedback from "./Element/Feedback";

type ErrorMessageLike = {
    props?: {
        error?: {
            code?: string;
            message?: string;
        };
    };
};

type FormCheckboxErrorMessage = React.ReactNode | ErrorMessageLike;

function isErrorMessageLike(value: unknown): value is ErrorMessageLike {
    return (
        typeof value === "object" &&
        value !== null &&
        "props" in value &&
        typeof (value as { props?: unknown }).props === "object"
    );
}

export interface FormCheckboxProps {
    error?: boolean;
    errorMessage?: FormCheckboxErrorMessage;
    labelProps?: LabelProps & { label: React.ReactNode };
    inputProps: CheckboxProps;
    fieldGroupProps?: FieldGroupProps;
    fieldControlProps?: FieldControlProps;
}

const FormCheckbox: React.FC<FormCheckboxProps> = ({
    error = false,
    errorMessage,
    labelProps,
    inputProps,
    fieldGroupProps,
    fieldControlProps,
}) => {
    const intl = useContext(IntlContext);
    const disabled = inputProps.disabled;
    const required = inputProps.required;

    const errorCode = isErrorMessageLike(errorMessage) ? errorMessage.props?.error?.code : undefined;
    const fallbackMessage: React.ReactNode =
        typeof errorMessage === "string" || typeof errorMessage === "number" || React.isValidElement(errorMessage)
            ? errorMessage
            : isErrorMessageLike(errorMessage)
              ? errorMessage.props?.error?.message
              : undefined;

    return (
        <FieldGroup error={error} {...fieldGroupProps}>
            {labelProps !== undefined && (
                <Label htmlFor={inputProps.id} disabled={disabled} error={error} required={required} {...labelProps}>
                    {labelProps.label}
                </Label>
            )}
            <FieldControl className={"field-control-checkbox"} error={error} disabled={disabled} {...fieldControlProps}>
                <Checkbox disabled={disabled} {...inputProps} />
            </FieldControl>
            {error && (errorCode || fallbackMessage) && (
                <Feedback className="field__feedback error">
                    {errorCode ? intl.formatMessage(`error.${errorCode}`) : fallbackMessage}
                </Feedback>
            )}
        </FieldGroup>
    );
};

export default FormCheckbox;
